import { StatefulService } from '@app/service/stateful.service';
import { isIterativePage } from '../helper/page-utility/page.utility';
import { PATH_PAGE_ITERATION_SYMBOL } from './stateful-value.class';
import { WorkflowPage } from './workflow.type';

export const PAGE_ITERATION_ROUTE_ID = 'page-iteration-id';

/**
 * An extension helper for page route helper
 */
export class PageRouteHelper {
  constructor(private readonly statefulService: StatefulService, private readonly page: WorkflowPage) {
    this.test();
  }

  /**
   * if ID/Path has #, it should be replaced with  ":page-iteration-id", and dashes around the hash by slash
   * borrower-info-# -> borrower-info/:page-iteration-id
   * #-borrower-info -> :page-iteration-id/borrower-info
   * borrower-#-info -> borrower/:page-iteration-id/info
   */
  public get PageRoutePath(): string {
    if (!this.IsIterativePage) {
      return this.page.path;
    }

    const iterationWithSlash = this.IterativeSymbolWithDash.replace('-', '/').replace(
      PATH_PAGE_ITERATION_SYMBOL,
      `:${PAGE_ITERATION_ROUTE_ID}`
    );
    return this.page.path.replace(this.IterativeSymbolWithDash, iterationWithSlash);
  }

  /**
   * return the page path based on iteration id
   * if page is not iterative, it will return the regular path
   */
  public getPagePath(pageIterationId?: number): string {
    if (!this.IsIterativePage || pageIterationId == null) {
      return this.page.path;
    }

    return this.PageRoutePath.replace(`:${PAGE_ITERATION_ROUTE_ID}`, String(pageIterationId));
  }

  /**
   * if page is iterative return page size
   */
  public get iterativePageSize(): number {
    if (this.IsIterativePage && this.page.array) {
      return this.statefulService.arraySize(this.page.array);
    }
    return 0;
  }

  /** Make sure page iteration symbol will follow with  */
  private test(): void {
    if (!this.page.path) {
      throw new Error(`There is no path in the page Id ${this.page.id}.`);
    }

    if (this.IsIterativePage) {
      if (this.IterativeSymbolWithDash.indexOf('-') === -1) {
        throw new Error(`In the iterative page Id ${this.page.id}, # symbol must be surrounded by dash. e.g. "borrower-info-#"`);
      }
    }
  }

  /** Indicate if the page is iterative by finding # in the page path/id */
  private get IsIterativePage(): boolean {
    return isIterativePage(this.page);
  }

  /**
   * if path is iterative return # with following and/or preceding dash
   * borrower-info-# -> -#
   * #-borrower-info -> #-
   * borrower-#-info -> -#-
   * borrower#info -> ''
   */
  private get IterativeSymbolWithDash(): string {
    if (!this.IsIterativePage) {
      return '';
    }
    const elements = this.parseIterativePath();
    return `${elements.before}${PATH_PAGE_ITERATION_SYMBOL}${elements.after}`;
  }

  private parseIterativePath(): { before: string; after: string } {
    let after = '';
    let before = '';
    const symbolIdx = this.page.path.indexOf(PATH_PAGE_ITERATION_SYMBOL);

    // At the begin #-borrower-info
    if (symbolIdx === 0) {
      after = this.page.path[symbolIdx + 1];
      // At the end borrower-info-#
    } else if (symbolIdx === this.page.path.length - 1) {
      before = this.page.path[symbolIdx - 1];
      // In the middle borrower-#-info
    } else if (symbolIdx !== -1) {
      after = this.page.path[symbolIdx + 1];
      before = this.page.path[symbolIdx - 1];
    }
    return { before, after };
  }
}
